import FormSelect from '@c/forms/controls/FormSelect';
import { Brand, getBrandAssets } from '@c/ShopByManufacturer';
import { useQueryClient } from '@tanstack/react-query';
import { uploadGarageImage } from '@util/firestore/listing';
import { addUserBike, editUserBike } from '@util/firestore/users';
import useBikes from '@util/hooks/useBikes';
import { isMobile } from '@util/index';
import { logError } from '@util/logError';
import { getCdnImageUrls } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { Bike, Make, UserBike } from 'models/shared';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import BaseModal from './BaseModal';
import { useToastContext } from 'context/ToastContext';
import MobileDrawer from '@c/drawers/MobileDrawer';

interface BikeFormProps {
  dismiss: () => void;
  bikeData?: UserBike;
  isEdit?: boolean;
  index?: number;
}

export const AddBikeForm = ({
  dismiss,
  bikeData,
  isEdit,
  index,
}: BikeFormProps) => {
  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = React.useState('');
  const { showErrorToast, showSuccessToast } = useToastContext();

  const { control, setValue, handleSubmit, watch } = useForm<UserBike>({
    defaultValues: {
      ...(isEdit && bikeData?.year && { year: bikeData.year }),
      ...(isEdit && bikeData?.make && { make: bikeData.make }),
      ...(isEdit && bikeData?.model && { model: bikeData.model }),
      ...(isEdit && bikeData?.imageUrl && { imageUrl: bikeData.imageUrl }),
      ...(isEdit && bikeData?.type && { type: bikeData.type }),
    },
  });

  const form = watch();

  const { bikeYears, bikeMakes, bikeModels } = useBikes(
    [`${form.year ?? ''}`],
    [form.make ?? ''] as Make[],
    [form.type ?? ''] as ('Dirt Bikes' | 'ATV' | 'UTV' | 'Street')[]
  );

  const submit = async (form: UserBike) => {
    if (!userDoc) return;

    if (!form.model || !form.make || !form.year) {
      showErrorToast('Please fill the required fields');

      return;
    }

    try {
      if (isEdit && index !== undefined) {
        await editUserBike(userDoc, form, index);
        dismiss();
      } else {
        if (
          userDoc.bikes.findIndex(
            (b) =>
              b.model === form.model &&
              b.year === form.year &&
              b.make === form.make
          ) === -1
        ) {
          await addUserBike(userDoc.uid, form);

          showSuccessToast('Bike added to your garage');

          dismiss();
        } else {
          setError('Bike already exists in your garage');
        }
      }
    } catch (e) {
      logError(e);
    }
  };

  return (
    <form
      className="flex w-full flex-col gap-[2.4rem] overflow-y-visible p-8 px-8 sm:w-[45rem]"
      onSubmit={handleSubmit(submit)}
    >
      {error && <span className="text-sm text-red-500">{error}</span>}

      <h3 className={'-mb-6 text-[1.6rem] font-semibold'}>
        Ride Type <span className="text-brand-secondary">*</span>
      </h3>

      <FormSelect
        allowEmpty={false}
        className="!text-[1.8rem]"
        control={control}
        name="type"
        placeholder="Select Type"
        options={[
          {
            label: 'Dirt Bikes',
            value: 'Dirt Bikes',
            id: 'DIRT_BIKE',
          },
          {
            label: 'ATV',
            value: 'ATV',
            id: 'ATV',
          },
          {
            label: 'UTV',
            value: 'UTV',
            id: 'UTV',
          },
          {
            label: 'Street',
            value: 'Street',
            id: 'STREET',
          },
        ]}
      />

      <div className="h-px w-full bg-brand-lightest-gray" />

      <FormSelect
        allowEmpty={false}
        control={control}
        name="year"
        placeholder="Select Year*"
        className="!text-[1.8rem]"
        options={bikeYears.map((y) => ({
          label: y.toString(),
          value: y,
          id: y.toString(),
        }))}
        defaultValue={form.year}
      ></FormSelect>

      <FormSelect
        allowEmpty={false}
        control={control}
        name="make"
        placeholder="Select Make*"
        className="!text-[1.8rem]"
        options={bikeMakes.sort().map((y) => ({
          label: y,
          value: y,
          id: y,
        }))}
        defaultValue={form.make}
      ></FormSelect>

      <FormSelect
        allowEmpty={false}
        control={control}
        name="model"
        disabled={!form.year || !form.make || bikeModels.length === 0}
        placeholder={
          form.year && form.make
            ? bikeModels.length > 0
              ? 'Select Model'
              : 'No Models Found'
            : 'Select Year and Make First'
        }
        className="!text-[1.8rem]"
        options={bikeModels
          .sort((a, b) => a.localeCompare(b))
          .map((y) => ({
            label: y,
            value: y,
            id: y,
          }))}
        defaultValue={form.model}
      ></FormSelect>
      <div className="flex flex-col gap-[1.6rem] sm:flex-row">
        <button
          type="button"
          className="hidden h-[5rem] w-full items-center justify-center rounded-[1.5rem] border border-zinc-200 bg-[#EDEDED] px-4 text-center text-[1.8rem] font-semibold text-black sm:flex sm:w-[30rem]"
          onClick={() => dismiss()}
        >
          Cancel
        </button>

        <div className="flex w-full justify-center">
          {bikeData && isEdit ? (
            <button
              className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary px-4 text-center text-[1.8rem] font-semibold text-white disabled:bg-zinc-400 sm:w-[30rem]"
              onClick={handleSubmit(submit)}
            >
              Save Changes
            </button>
          ) : (
            <button
              className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary px-4 text-center text-[1.8rem] font-semibold text-white disabled:bg-zinc-400 sm:w-[30rem]"
              onClick={handleSubmit(submit)}
              disabled={!form.year || !form.make || !form.model}
            >
              Add to Garage
            </button>
          )}
        </div>

        <button
          type="button"
          className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] border border-zinc-200 bg-[#EDEDED] px-4 text-center text-[1.8rem] font-semibold text-black sm:hidden sm:w-[30rem]"
          onClick={() => dismiss()}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
type AddBikeModalProps = {
  isOpen: boolean;
  dismiss: () => void;
  bikeData?: any;
  isEdit?: boolean;
  index?: any;
};
const AddBikeModal = ({
  isOpen,
  dismiss,
  bikeData,
  isEdit,
  index,
}: AddBikeModalProps) => {
  if (isMobile()) {
    return (
      <MobileDrawer
        show={isOpen}
        dismiss={() => dismiss()}
        title={isEdit ? 'Edit Bike' : 'Add your vehicle'}
      >
        <AddBikeForm
          bikeData={bikeData}
          isEdit={isEdit}
          dismiss={() => dismiss()}
          index={index}
        />
      </MobileDrawer>
    );
  } else {
    return (
      <BaseModal
        isOpen={isOpen}
        dismiss={() => dismiss()}
        title={isEdit ? 'Edit Bike' : 'Add your vehicle'}
        isFullScreen={isMobile()}
        overflowType="visible"
      >
        <AddBikeForm
          bikeData={bikeData}
          isEdit={isEdit}
          dismiss={() => dismiss()}
          index={index}
        />
      </BaseModal>
    );
  }
};

export default AddBikeModal;
